// Govard CRM — web shell root.
//
// Wires the top-level providers that every route relies on:
//   - <I18nProvider>       → ru catalog (shared + crm namespaces).
//   - <AnalyticsProvider>  → stdout-only client in Phase 1 (ADR-0002).
//   - <FlagsProvider>      → env-backed feature flags (pro-mode, ai-chat).
//   - <RouterProvider>     → v7 router, see ./router.tsx.
//
// The analytics + flags ctx are memoised at module scope so their identity
// stays stable across re-renders (both providers compare by reference).

import { useEffect, useMemo, type ReactElement, type ReactNode } from 'react';
import { QueryClientProvider } from '@tanstack/react-query';
import { App as AntdApp, ConfigProvider } from 'antd';
import enUS from 'antd/locale/en_US';
import ruRU from 'antd/locale/ru_RU';
import { RouterProvider } from 'react-router-dom';

import { AnalyticsProvider } from '@govard/analytics';
import { FlagsProvider } from '@govard/feature-flags/react';
import { I18nProvider, useTranslation } from '@govard/i18n-core/react';

import { createAnalytics } from './analytics/index.js';
import { buildFlagsCtx, flagsRegistry } from './feature-flags/index.js';
import { i18n } from './i18n/index.js';
import { router } from './router.js';
import { ErrorBoundary } from './shared/ErrorBoundary.js';
import { queryClient } from './shared/query/queryClient.js';
import { useScopeStore } from './shared/scope/useCurrentScope.js';
import { ThemeProvider, useTheme } from './theme/ThemeContext.js';
import { NativeTitleTooltips } from './components/layout/NativeTitleTooltips.js';

// Single instance for the lifetime of the tab. Analytics buffers + dedupe
// state MUST NOT reset on every render.
const analytics = createAnalytics();

function LocaleProvider({ children }: { children: ReactNode }): ReactElement {
  const { locale } = useTranslation();
  const { antdTheme } = useTheme();

  useEffect(() => {
    document.documentElement.lang = locale;
  }, [locale]);

  return (
    <ConfigProvider theme={antdTheme} locale={locale === 'ru' ? ruRU : enUS}>
      <AntdApp style={{ height: '100%' }}>
        {children}
        <NativeTitleTooltips />
      </AntdApp>
    </ConfigProvider>
  );
}

export function App(): ReactElement {
  const scope = useScopeStore((state) => state.scope);
  const user = useScopeStore((state) => state.user);
  const workspaceSettings = useScopeStore((state) => state.workspaceSettings);
  const flagsCtx = useMemo(
    () => buildFlagsCtx({ scope, user, workspaceSettings }),
    [scope, user, workspaceSettings],
  );

  return (
    <I18nProvider i18n={i18n}>
      <ThemeProvider>
        <LocaleProvider>
          <AnalyticsProvider client={analytics}>
            <FlagsProvider registry={flagsRegistry} ctx={flagsCtx}>
              <QueryClientProvider client={queryClient}>
                <ErrorBoundary>
                  <RouterProvider router={router} />
                </ErrorBoundary>
              </QueryClientProvider>
            </FlagsProvider>
          </AnalyticsProvider>
        </LocaleProvider>
      </ThemeProvider>
    </I18nProvider>
  );
}
